import { Fragment, useEffect } from "react";
import {
  Box,
  Button,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Dropzone, DropzoneProps, DropzoneText } from "~/components/dropzone";
import {
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

import { Command } from "~/components/guides/components";

import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  IntegrationType,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { useSnackbar } from "notistack";
import { AnimatePresence, motion } from "motion/react";
import { fadeInOut } from "~/lib/animations";
import { useNavigate } from "react-router-dom";
import { IntegrationAddHeader } from "../../headers/IntegrationAddHeader";
import { getError } from "~/lib/handle-error";
import {
  helperTextStyles,
  ValidationMessage,
} from "../../utils/integrationValidationUtils";
import useGenerateIntegrationName from "../../utils/integrationsUtils";
import { UpdateFlowData } from "../../utils/integrationsTypes";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";
import { GreenCheckCircleIcon } from "~/components/GreenCheckCircleIcon";

type BigQueryFormInput = {
  integrationName: string;
  datasetId: string;
  serviceAccount: string | null;
  wifAudience: string;
  wifServiceAccountEmail: string;
};

const defaultValues: BigQueryFormInput = {
  integrationName: "",
  datasetId: "",
  serviceAccount: null,
  wifAudience: "",
  wifServiceAccountEmail: "",
};

export function BigQueryIntegrationForm({
  scope,
  updateFlow,
}: {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}) {
  let navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const defaultIntegrationName = useGenerateIntegrationName({ scope });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const integrationMrn = data.createClientIntegration.integration.mrn;
      triggerClientIntegrationScan({
        variables: {
          input: { mrn: integrationMrn, type: ActionType.RunExport },
        },
      });
    },
    refetchQueries: [
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    onError(error) {
      console.log("%c Error Scheduling scan on creation", "color: tomato");
      console.log(error.message);
    },
  });

  useEffect(() => {
    if (updateFlow) {
      if (
        updateFlow.integration.configurationOptions?.__typename ===
        "BigqueryConfigurationOptions"
      ) {
        reset({
          integrationName: updateFlow.integration.name,
          datasetId: updateFlow.integration.configurationOptions.datasetId,
          wifAudience:
            updateFlow.integration.configurationOptions.wifAudience ?? "",
          wifServiceAccountEmail:
            updateFlow.integration.configurationOptions
              .wifServiceAccountEmail ?? "",
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const [serviceAccount, wifAudience] = watch([
    "serviceAccount",
    "wifAudience",
  ]);

  const onSubmit: SubmitHandler<BigQueryFormInput> = async (data) => {
    const datasetId = data.datasetId.trim();
    const serviceAccount = data.serviceAccount?.trim() || "";
    const wifAudience = data.wifAudience?.trim() || undefined;
    const wifServiceAccountEmail =
      data.wifServiceAccountEmail?.trim() || undefined;
    const bigqueryConfigurationOptions = {
      datasetId,
      serviceAccount,
      wifAudience,
      wifServiceAccountEmail,
    };

    try {
      if (updateFlow) {
        const integrationId = updateFlow.integration.mrn.split("/").pop();
        const mrn = getIntegrationMrn({ scope, integrationId });
        await updateIntegration({
          variables: {
            input: {
              mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.Bigquery,
              configurationOptions: {
                bigqueryConfigurationOptions,
              },
            },
          },
        });

        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        navigate(
          `/space/integrations/bigquery/${integrationId}/?${scope.params}`,
        );
      } else {
        await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.Bigquery,
              longLivedToken: false,
              configurationOptions: {
                bigqueryConfigurationOptions,
              },
            },
          },
        });
        navigate(`/space/integrations/bigquery?${scope.params}`);
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const onDropAccepted: DropzoneProps["onDropAccepted"] = async (files) => {
    setValue("serviceAccount", await files[0].text());
  };

  setDocumentTitle(["BigQuery", "Integrations Setup"]);

  return (
    <Fragment>
      <IntegrationAddHeader {...{ type: IntegrationType.Bigquery }} />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Step 1 */}
          <Box pb={4}>
            <Command
              number={1}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Choose an integration name
            </Command>

            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Please choose a descriptive name that lets you easily identify
                your integration.
              </Typography>
              <Controller
                name="integrationName"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="Your integration name..."
                    error={Boolean(errors.integrationName)}
                    helperText={
                      Boolean(errors.integrationName) && (
                        <ValidationMessage error={errors.integrationName} />
                      )
                    }
                  />
                )}
              />
            </Box>
          </Box>

          {/* Step 2 */}
          <Box pb={4}>
            <Command
              number={2}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Enter the dataset ID
            </Command>

            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Find the dataset ID in your target dataset’s properties. To
                learn more, read the{" "}
                <Link
                  href="https://docs.cloud.google.com/bigquery/docs/listing-datasets"
                  target="_blank"
                  rel="noopener"
                >
                  Google documentation
                </Link>
                .
              </Typography>
              <Controller
                name="datasetId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="project-id.dataset_id"
                    error={Boolean(errors.datasetId)}
                    helperText={
                      getFieldState("datasetId").isTouched &&
                      Boolean(errors.datasetId) && (
                        <ValidationMessage
                          error={errors.datasetId}
                          integrationTypeId="bigquery"
                        />
                      )
                    }
                  />
                )}
              />
            </Box>
          </Box>

          {/* Step 3 - Authentication */}
          <Box pb={4}>
            <Command
              number={3}
              options={{
                fontSize: { xs: 16 },
                dotColor: theme.palette.background.lightest,
              }}
            >
              Configure authentication
            </Command>

            <Box pl={6}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 2 }}
              >
                Choose one of the following authentication methods:
              </Typography>

              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Option A: Workload Identity Federation (recommended)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use keyless authentication with Workload Identity Federation. To
                learn more, read the{" "}
                <Link
                  href="https://cloud.google.com/iam/docs/workload-identity-federation"
                  target="_blank"
                  rel="noopener"
                >
                  Google documentation
                </Link>
                .
              </Typography>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                WIF Audience URL
              </Typography>
              <Controller
                name="wifAudience"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      mb: 3,
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="https://iam.googleapis.com/projects/PROJECT_NUMBER/locations/global/workloadIdentityPools/POOL_ID/providers/PROVIDER_ID"
                  />
                )}
              />
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Service Account Email (for impersonation)
              </Typography>
              <Controller
                name="wifServiceAccountEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{
                      background: theme.palette.code.background,
                      borderRadius: 1,
                      color: "text.primary",
                      ...helperTextStyles,
                    }}
                    placeholder="service-account@project.iam.gserviceaccount.com"
                  />
                )}
              />

              <Typography
                fontWeight={700}
                sx={{ mb: 1, mt: 4, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    height: "1px",
                    bgcolor: "divider",
                    mr: 2,
                  }}
                />
                OR
                <Box
                  component="span"
                  sx={{
                    flex: 1,
                    height: "1px",
                    bgcolor: "divider",
                    ml: 2,
                  }}
                />
              </Typography>

              <Typography fontWeight={700} sx={{ mb: 1, mt: 3 }}>
                Option B: Service Account JSON Key
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a service account JSON key file. To learn more, read the{" "}
                <Link
                  href="https://mondoo.com/docs/reporting/export/bigquery-integration"
                  target="_blank"
                  rel="noopener"
                >
                  Mondoo documentation
                </Link>{" "}
                and the{" "}
                <Link
                  href="https://docs.cloud.google.com/iam/docs/service-accounts-create"
                  target="_blank"
                  rel="noopener"
                >
                  Google documentation
                </Link>
                .
              </Typography>
              {!updateFlow && (
                <AnimatePresence mode="wait">
                  {!serviceAccount ? (
                    <Box key="dropzone" component={motion.div} {...fadeInOut}>
                      <Dropzone
                        className="mondoo-dropzone-area"
                        accept={{ "application/json": [] }}
                        multiple={false}
                        showAlerts={["error"]}
                        onDropAccepted={onDropAccepted}
                      >
                        <DropzoneText>
                          Drag and drop your .json file here.
                        </DropzoneText>
                      </Dropzone>
                    </Box>
                  ) : (
                    <Box
                      key="success"
                      component={motion.div}
                      {...fadeInOut}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6">
                          Successfully loaded JSON file
                        </Typography>
                        <GreenCheckCircleIcon fontSize="large" sx={{ ml: 2 }} />
                      </Box>

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setValue("serviceAccount", "")}
                      >
                        Remove JSON file
                      </Button>
                    </Box>
                  )}
                </AnimatePresence>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              endIcon={<ChevronRightIcon />}
              disabled={
                updateFlow
                  ? !isValid
                  : !isValid || (!serviceAccount && !wifAudience)
              }
            >
              {updateFlow ? "update configuration" : "create integration"}
            </Button>
          </Box>
        </form>
      </Box>
    </Fragment>
  );
}
